/**
 * The docbody row of a body stored per block (docBlocks.js) — the "head" —
 * and how to tell it from a body stored whole. Pure functions of a row, kept
 * apart from documentsStore so the editor can use them wherever the store
 * itself is stubbed.
 */
import { compactSnapshot, snapshotFromBodyData } from './docBodyCodec';
import { stableStringify } from '../../../components/ui/lexicalBlocks';
import { OUTBOX_ITEM_MAX_BYTES } from '../../../records/syncLimits';

/**
 * True when the docbody row holds the body whole, newer than any blocks: a
 * body not moved to blocks yet, or one an older app version saved after the
 * move.
 *
 * While older versions are around, the head of a body stored per block keeps
 * a whole snapshot for them (headData: `bodyHtml` + compact `bodyBlocks`,
 * real block ids), signed (`htmlSig`, `snapSig`). An older app's save
 * replaces the row on its own device (dropping `blocks`) and reaches other
 * devices as changed keys only (keeping `blocks` and the signatures there):
 * a part that no longer matches its signature is that save. A head marked
 * `htmlOmitted` (a page too large for one record, shown to older apps as an
 * "update" notice) is never taken over that way — an edit of the notice must
 * not wipe the page.
 */
export function isWholeBody(body) {
  if (!body) return false;
  if (!body.blocks) return true;
  if (body.htmlOmitted) return false;
  if (body.htmlSig !== sig(body.bodyHtml || '')) return true;
  // A head written before it carried a block snapshot (no `snapSig`): a whole
  // snapshot on it now can only be an older app's save.
  if (body.snapSig === undefined) return body.bodyBlocks != null || body.bodyLexical != null;
  return body.snapSig !== sig(stableStringify(compactOf(body)));
}

/**
 * The head's own snapshot when it is intact (both signatures match): the body
 * as of the last refresh, with the real block ids — what a device opens
 * before the block records themselves have arrived.
 */
export function headSnapshot(body) {
  if (!body || !body.blocks || body.htmlOmitted || isWholeBody(body)) return null;
  return body.bodyBlocks != null ? snapshotFromBodyData(body) : null;
}

/** The editor snapshot a whole docbody carries — none when an older app wrote only HTML over a head snapshot. */
export function wholeSnapshot(body) {
  if (!body) return null;
  if (body.blocks && body.snapSig === sig(stableStringify(compactOf(body)))) return null; // HTML-only save over our snapshot
  return snapshotFromBodyData(body);
}

/** A docbody's whole snapshot in compact form, whichever field it arrived in (the live path expands it). */
function compactOf(body) {
  if (body.bodyBlocks != null) return body.bodyBlocks;
  if (body.bodyLexical != null) return compactSnapshot(body.bodyLexical) ?? body.bodyLexical;
  return null;
}

/** A short signature (FNV-1a + length), stored beside the part it signs. */
function sig(text) {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) h = Math.imul(h ^ text.charCodeAt(i), 0x01000193) >>> 0;
  return `${h.toString(36)}.${text.length.toString(36)}`;
}

/** What an older app version shows for a page too large to keep a whole copy of. */
const OMITTED_NOTICE = '<p>This page is too large for this version of Apiboo. Update Apiboo to open it.</p>';

/**
 * The docbody row of a body stored per block: the head, with a whole
 * snapshot for older app versions (HTML + compact blocks with the real block
 * ids, so their live edits line up with ours) and its signatures
 * (isWholeBody). Every key is written, null or false included: a sync push
 * sends changed keys only, so a dropped key would stay on the server. A page
 * whose snapshot would not fit in one synced record gets the update notice
 * instead (`htmlOmitted`) — its blocks still sync, and a too-large row would
 * mark the whole document "not syncing" (syncSizeStore).
 */
export function headData(docId, bodyHtml, snapshot) {
  const bodyBlocks = snapshot ? compactSnapshot(snapshot) : null;
  const data = {
    scope_id: docId,
    blocks: true,
    bodyHtml,
    bodyBlocks,
    bodyLexical: null,
    htmlOmitted: false,
    htmlSig: sig(bodyHtml),
    snapSig: sig(stableStringify(bodyBlocks)),
  };
  if (JSON.stringify(data).length <= OUTBOX_ITEM_MAX_BYTES - 2000) return data;
  return {
    ...data,
    bodyHtml: OMITTED_NOTICE,
    bodyBlocks: null,
    htmlOmitted: true,
    htmlSig: sig(OMITTED_NOTICE),
    snapSig: sig(stableStringify(null)),
  };
}
